import { Box, IconButton, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

/** Labels only — presence without identities (OneBilly privacy stance). */
const collectEvents = (nodes) => {
  const out = [];
  (nodes || []).forEach((n) => {
    const lastRun = n?.data?.output_data?.last_run;
    if (!lastRun?.at) return;
    out.push({
      id: `${n.id}:${lastRun.at}`,
      label: n.data?.label || n.data?.type || 'package',
      status: lastRun.status || '',
      trigger: lastRun.trigger || '',
      at: lastRun.at,
    });
  });
  return out
    .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
    .slice(0, 10);
};

const timeAgo = (iso) => {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  return `${Math.round(s / 3600)}h ago`;
};

/**
 * Ceiling ticker analog: real runs scrolling past, labels not identities.
 * Fed by live node state (WebSocket-merged), so it moves when the canvas does.
 */
export default function ActivityTicker({ nodes }) {
  const events = useMemo(() => collectEvents(nodes), [nodes]);
  const [idx, setIdx] = useState(0);
  const [, forceTick] = useState(0);
  const [hidden, setHidden] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.localStorage.getItem('canvas-ticker-hidden') === '1'
  );

  useEffect(() => {
    if (events.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => i + 1), 4000);
    return () => clearInterval(t);
  }, [events.length]);

  useEffect(() => {
    const t = setInterval(() => forceTick((n) => n + 1), 15000); // refresh "ago"
    return () => clearInterval(t);
  }, []);

  const hide = () => {
    setHidden(true);
    try {
      window.localStorage.setItem('canvas-ticker-hidden', '1');
    } catch {
      /* private mode */
    }
  };

  if (hidden || !events.length) return null;
  const ev = events[idx % events.length];
  const mark =
    ev.status === 'ok' ? '✓' : ev.status === 'running' ? '…' : ev.status ? '✗' : '·';

  return (
    <Box
      sx={{
        position: 'absolute',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: 10,
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1.25,
        py: 0.5,
        borderRadius: 2,
        bgcolor: 'rgba(12,15,28,0.72)',
        border: '1px solid rgba(255,255,255,0.08)',
        maxWidth: 'min(64%, 560px)',
        pointerEvents: 'auto',
      }}
    >
      <Typography
        variant="caption"
        noWrap
        title={ev.label}
        sx={{
          fontFamily: 'var(--canvas-font-mono, ui-monospace, monospace)',
          color: 'rgba(255,255,255,0.78)',
          letterSpacing: '0.02em',
        }}
      >
        {mark} {ev.label}
        {ev.trigger ? ` · ${ev.trigger}` : ''} · {timeAgo(ev.at)}
      </Typography>
      <IconButton
        size="small"
        aria-label="Hide activity ticker"
        onClick={hide}
        sx={{ ml: 0.5, color: 'rgba(255,255,255,0.5)', p: 0.25 }}
      >
        ×
      </IconButton>
    </Box>
  );
}
